import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { TvController } from '@/lib/schema/tvController';
import { TvControllerGroup } from '@/lib/schema/tvControllerGroup';
import { useEffect, useMemo, useState } from 'react';

type GroupMembersDialogProps = {
  group: TvControllerGroup | null;
  onOpenChange: (open: boolean) => void;
  tvControllers: TvController[];
  onSubmit: (group: TvControllerGroup, memberIds: string[]) => Promise<void>;
};

export function GroupMembersDialog({
  group,
  onOpenChange,
  tvControllers,
  onSubmit,
}: GroupMembersDialogProps) {
  const [filter, setFilter] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (group) {
      setFilter('');
      setMemberIds(
        group.tag
          ? tvControllers
              .filter((tv) => tv.id && tv.tags?.includes(group.tag!))
              .map((tv) => tv.id!)
          : []
      );
    }
    // Intentionally not re-resetting on TV snapshot updates mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  const rows = useMemo(
    () =>
      tvControllers
        .filter((tv) => tv.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tvControllers]
  );

  const filteredRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (tv) => tv.name.toLowerCase().includes(q) || tv.ip.includes(q)
    );
  }, [rows, filter]);

  const selected = new Set(memberIds);
  const allFilteredSelected =
    filteredRows.length > 0 && filteredRows.every((tv) => selected.has(tv.id!));

  return (
    <Dialog open={!!group} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>TVs in "{group?.name}"</DialogTitle>
          <DialogDescription>
            A TV can belong to any number of groups.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Input
              autoComplete="off"
              placeholder="Filter by name or IP ..."
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => {
                const union = new Set(memberIds);
                filteredRows.forEach((tv) => union.add(tv.id!));
                setMemberIds([...union]);
              }}
              disabled={filteredRows.length === 0 || allFilteredSelected}
            >
              Select all ({filteredRows.length})
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => {
                const filteredIds = new Set(filteredRows.map((tv) => tv.id!));
                setMemberIds(memberIds.filter((id) => !filteredIds.has(id)));
              }}
              disabled={!filteredRows.some((tv) => selected.has(tv.id!))}
            >
              Clear
            </Button>
          </div>
          <div className="rounded-md border max-h-72 overflow-y-auto">
            {filteredRows.length ? (
              filteredRows.map((tv) => (
                <label
                  key={tv.id}
                  className="flex items-center gap-3 px-3 py-1.5 border-b last:border-b-0 cursor-pointer hover:bg-muted/50"
                >
                  <Checkbox
                    checked={selected.has(tv.id!)}
                    onCheckedChange={(checked) =>
                      setMemberIds(
                        checked === true
                          ? [...memberIds, tv.id!]
                          : memberIds.filter((id) => id !== tv.id)
                      )
                    }
                  />
                  <span className="text-sm">{tv.name}</span>
                  <span className="ml-auto font-mono text-xs text-muted-foreground">
                    {tv.ip}
                  </span>
                </label>
              ))
            ) : (
              <p className="p-4 text-center text-sm text-muted-foreground">
                No TVs match "{filter}"
              </p>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            {memberIds.length} TV(s) selected
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSaving}
            onClick={async () => {
              if (!group) return;
              setIsSaving(true);
              try {
                await onSubmit(group, memberIds);
                onOpenChange(false);
              } catch {
                // The page already toasted the failure; keep the dialog open.
              } finally {
                setIsSaving(false);
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
